/** @jsxImportSource @emotion/react */
import {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
  Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { Button, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { DefaultProps } from '../MdIvesCmpuRegPage';
import {
  completeMdIves,
  deleteMdPrjInfo,
  dropMdIves,
  findMdPrjInfo,
  inProgressMdIves,
  saveMdPrjInfo,
} from 'apis/md/MdProject';
import { MdIvesPrjInfo } from 'models/md/MdIves';
import CustomTextarea from 'components/inputs/CustomTextarea';
import { useNavigate } from 'react-router-dom';
import Dropzone from 'components/dropzone/Dropzone';
import { FileSaveResponse, FileSaveResult } from 'models/admin/FileInfo';
import UserMultiModal from 'components/modals/common/UserMultiModal';
import { NormalButton } from 'components/buttons/ButtonStyle';
import EltrCellLineInqPopup from '../../popup/EltrCellLineInqPopup';
import CustomerRegistPopup from '../../popup/CustomerRegistPopup';
import { MdLine } from 'models/md/MdLine';
import { findMdCustomerList } from 'apis/md/MdCustomer';
import useSessionStore from 'stores/useSessionStore';
import { hasRole } from 'utils/SessionUtil';

interface prjInfoProps extends DefaultProps {
  getStatus: () => void;
}

const PrjInfoPage = forwardRef(
  (
    { elmPrjId, setElmPrjId, isEditable, getStatus, ...props }: prjInfoProps,
    ref: React.Ref<any>
  ) => {
    const { t } = useTranslation();
    const { isLoading, openLoading } = useLoading();
    const { openMessageBar } = useMessageBar();
    const { openCommonModal } = useCommonModal();
    const navigate = useNavigate();
    const { userId, roleCodes } = useSessionStore();

    const initPrjInfo: MdIvesPrjInfo = {
      elmPrjId: elmPrjId || '',
      elmPrjNo: '',
      elmPrjTpCd: '',
      cellFertTpCd: '',
      elmPrjNm: '',
      elmPrjDesc: '',
      copCd: '',
      bildPlntCds: [],
      bildPlntCd: '',
      bildPlntCdNm: '',
      prjDivsCd: '',
      prjId: '',
      prjNm: '',
      ivesStDt: '',
      ivesEndDt: '',
      elmPrjStatCd: '',
      elmPrjStatCdNm: '',
      eltrLnNm: '',
      cellLnNm: '',
      mduLnNm: '',
      custNm: '',
      wbsCd: '',
      ivesAmt: '',
      bmkYn: '',
      rmk: '',
      atchFileGrId: '',
      useYn: '',
      optValCtn1: '',
      optValCtn2: '',
      optValCtn3: '',
      optValCtn4: '',
      optValCtn5: '',
      dataInsUserId: '',
      dataInsUserNm: '',
      dataInsDtm: '',
      dataUpdUserId: '',
      dataUpdUserNm: '',
      dataUpdDtm: '',
      mdlNm: '',
      mktgMgr: '',
      ivesMgr: '',
      mktgManagers: [],
      ivesManagers: [],
      eltrLines: [],
      cellLines: [],
      plants: [],
      repCellLnId: '',
      repEltrLnId: '',
      repPlntCd: '',
    };

    const dropzoneRef = useRef<any>();
    const [errors, setErrors] = useState<any>();
    const [prjInfo, setPrjInfo] = useState<MdIvesPrjInfo>(initPrjInfo);
    const [duplPrjInfo, setDuplPrjInfo] = useState<MdIvesPrjInfo>(initPrjInfo);
    const [chkEltrLnList, setChkEltrLnList] = useState<MdLine[]>([]);
    const [chkCellLnList, setChkCellLnList] = useState<MdLine[]>([]);
    const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
    const [customerList, setCustomerList] = useState<Code[]>([]);
    const [popups, setPopups] = useState({
      eltrLn: false,
      cellLn: false,
      customer: false,
      mktgMgr: false,
      ivesMgr: false,
    });

    const repPlants = useMemo(() => {
      return bildPlntCode.filter((o) => prjInfo.plants?.some((x) => x.bildPlntCd === o.cmnCd));
    }, [prjInfo.plants]);

    const repEltrLines = useMemo(() => {
      return (prjInfo.eltrLines || []).map((o) => ({
        cmnCd: o.prdnLnId,
        cmnCdNm: o.bildPlntCd + ' ' + o.prdnLnNm,
      }));
    }, [prjInfo.eltrLines]);

    const repCellLines = useMemo(() => {
      return (prjInfo.cellLines || []).map((o) => ({
        cmnCd: o.prdnLnId,
        cmnCdNm: o.bildPlntCd + ' ' + o.prdnLnNm,
      }));
    }, [prjInfo.cellLines]);

    /**
     * drop 버튼 활성화 조건
     * 1. 검토자
     * 2. ING 상태
     * 3. 형개발 투자 관리자(MD_IVES_MANAGER)
     */
    const isBtnDrop = () => {
      return (
        (hasRole('MD_IVES_MANAGER') ||
          hasRole('ADM') ||
          prjInfo.ivesManagers?.some((o) => o.wrtUserId === userId)) &&
        prjInfo.elmPrjStatCd === 'ING'
      );
    };

    /**
     * 진행중 버튼 활성화 조건
     * 1. 검토자
     * 2. drop / 완료 상태
     * 3. 형개발 투자 관리자(MD_IVES_MANAGER)
     */
    const isBtnInProgress = () => {
      const statList = ['DROP', 'CPLT'];
      return (
        (hasRole('MD_IVES_MANAGER') ||
          hasRole('ADM') ||
          prjInfo.ivesManagers?.some((o) => o.wrtUserId === userId)) &&
        statList.includes(prjInfo.elmPrjStatCd || '')
      );
    };

    /**
     * 저장 버튼 활성화 조건
     * 1. 형개발 투자 관리자(MD_IVES_MANAGER)
     * 2. DROP/완료 상태가 아님(신규등록 or ING)
     */
    const isBtnSave = () => {
      const STATUS = ['DROP', 'CPLT'];
      return (
        (hasRole('MD_IVES_MANAGER') ||
          hasRole('ADM') ||
          prjInfo.ivesManagers?.some((o) => o.wrtUserId === userId)) &&
        !STATUS.includes(prjInfo.elmPrjStatCd ?? '')
      );
    };

    /**
     * 완료 버튼 활성화 조건
     * 1. 검토자
     * 2. ING 상태
     * 3. 형개발 투자 관리자(MD_IVES_MANAGER)
     */
    const isBtnComplete = () => {
      return (
        (hasRole('MD_IVES_MANAGER') ||
          hasRole('ADM') ||
          prjInfo.ivesManagers?.some((o) => o.wrtUserId === userId)) &&
        prjInfo.elmPrjStatCd === 'ING'
      );
    };

    /**
     * 삭제 버튼 활성화 조건
     * 1. 검토자
     * 2. Drop 상태
     * 3. 형개발 투자 관리자(MD_IVES_MANAGER)
     */
    const isBtnDelete = () => {
      return (
        (hasRole('MD_IVES_MANAGER') ||
          hasRole('ADM') ||
          prjInfo.ivesManagers?.some((o) => o.wrtUserId === userId)) &&
        prjInfo.elmPrjStatCd === 'DROP'
      );
    };

    const initCodes = async () => {
      const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
      setBildPlntCode(bildPlntCode?.map((o) => ({ ...o })));
      const customerList = await findMdCustomerList({});
      setCustomerList(customerList?.map((o) => ({ cmnCd: o.custId, cmnCdNm: o.custNm })));
    };

    const getData = () => {
      if (elmPrjId) {
        openLoading(true);
        findMdPrjInfo(elmPrjId)
          .then((res) => {
            if (res.data && res.successOrNot === 'Y') {
              setPrjInfo({
                ...res.data,
                ivesMgr: res.data.ivesManagers?.map((o) => o.wrtUserNm).join(', '),
                mktgMgr: res.data.mktgManagers?.map((o) => o.wrtUserNm).join(', '),
                eltrLnNm: res.data.eltrLines
                  ?.map((o) => o.bildPlntCdNm + ' ' + o.prdnLnNm)
                  .join(', '),
                cellLnNm: res.data.cellLines
                  ?.map((o) => o.bildPlntCdNm + ' ' + o.prdnLnNm)
                  .join(', '),
                repPlntCd: res.data.repPlntCd || res.data.plants?.at(0)?.bildPlntCd || '',
                repEltrLnId: res.data.repEltrLnId || res.data.eltrLines?.at(0)?.prdnLnId || '',
                repCellLnId: res.data.repCellLnId || res.data.cellLines?.at(0)?.prdnLnId || '',
              });
              // 복사본
              setDuplPrjInfo({
                ...res.data,
                ivesMgr: res.data.ivesManagers?.map((o) => o.wrtUserNm).join(', '),
                mktgMgr: res.data.mktgManagers?.map((o) => o.wrtUserNm).join(', '),
                eltrLnNm: res.data.eltrLines
                  ?.map((o) => o.bildPlntCdNm + ' ' + o.prdnLnNm)
                  .join(', '),
                cellLnNm: res.data.cellLines
                  ?.map((o) => o.bildPlntCdNm + ' ' + o.prdnLnNm)
                  .join(', '),
                repPlntCd: res.data.repPlntCd || res.data.plants?.at(0)?.bildPlntCd || '',
                repEltrLnId: res.data.repEltrLnId || res.data.eltrLines?.at(0)?.prdnLnId || '',
                repCellLnId: res.data.repCellLnId || res.data.cellLines?.at(0)?.prdnLnId || '',
              });

              // 라인 수정 확인용 상태
              setChkEltrLnList(res.data?.eltrLines || []);
              setChkCellLnList(res.data?.cellLines || []);
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t(
                'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
                '요청 정보 조회 중 오류가 발생했습니다.'
              ),
            });
          })
          .finally(() => openLoading(false));
        getStatus();
      } else {
        btnReset();
      }
    };

    const handleOnChange = (name: string, value: any) => {
      if (name === 'plants') {
        setPrjInfo((prev) => ({
          ...prev,
          [name]: value.map((o) => ({ bildPlntCd: o })),
          repPlntCd: value.some((o) => o === prev.repPlntCd) ? prev.repPlntCd : value[0],
        }));
        setErrors((prev) => ({ ...prev, [name]: false, repPlntCd: false }));
      } else {
        setPrjInfo((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: false }));
      }
    };

    const btnList = () => {
      navigate('/md/ives-cmpu');
    };

    const btnReset = () => {
      setPrjInfo((prev) => ({ ...initPrjInfo, elmPrjStatCd: prev.elmPrjStatCd }));
      setErrors(null);
    };

    const isValid = () => {
      setErrors((prev) => ({
        ...prev,
        elmPrjNm: !prjInfo.elmPrjNm,
        plants: !prjInfo.plants?.length,
        eltrLnNm: !prjInfo.eltrLines?.length,
        cellLnNm: !prjInfo.cellLines?.length,
        repPlntCd: !prjInfo.repPlntCd,
        repEltrLnId: !prjInfo.repEltrLnId,
        repCellLnId: !prjInfo.repCellLnId,
        custId: !prjInfo.custId,
        ivesMgr: !prjInfo.ivesManagers?.length,
        mktgMgr: !prjInfo.mktgManagers?.length,
        elmPrjDesc: !prjInfo.elmPrjDesc,
      }));

      return (
        !!prjInfo.elmPrjNm &&
        !!prjInfo.plants?.length &&
        !!prjInfo.eltrLines?.length &&
        !!prjInfo.cellLines?.length &&
        !!prjInfo.repPlntCd &&
        !!prjInfo.repEltrLnId &&
        !!prjInfo.repCellLnId &&
        !!prjInfo.custId &&
        !!prjInfo.ivesManagers?.length &&
        !!prjInfo.mktgManagers?.length &&
        !!prjInfo.elmPrjDesc
      );
    };

    const btnInProgress = () => {
      if (!prjInfo.elmPrjId) {
        openMessageBar({
          type: 'error',
          content: t('com.msg.프로젝트를 등록하세요.', '프로젝트를 등록하세요.'),
        });
        return;
      }

      openCommonModal({
        modalType: 'confirm',
        content: t('com.msg.프로젝트를 진행 하시겠습니까?', '프로젝트를 진행 하시겠습니까?'),
        yesCallback: () => {
          openLoading(true);
          inProgressMdIves(prjInfo.elmPrjId ?? '')
            .then((res) => {
              if (res.successOrNot === 'Y') {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.msg.상태가 변경되었습니다.', '상태가 변경되었습니다.'),
                });
                getData();
              } else {
                openMessageBar({
                  type: 'error',
                  content: String(res.data),
                });
              }
            })
            .catch((err) => {
              openMessageBar({
                type: 'error',
                content: t(
                  'com.msg.데이터 저장 중 오류가 발생했습니다.',
                  '데이터 저장 중 오류가 발생했습니다.'
                ),
              });
            })
            .finally(() => openLoading(false));
        },
      });
    };

    const btnDelete = () => {
      if (!prjInfo.elmPrjId) {
        openMessageBar({
          type: 'error',
          content: t('com.msg.프로젝트를 등록하세요.', '프로젝트를 등록하세요.'),
        });
        return;
      }

      openCommonModal({
        modalType: 'confirm',
        content: t('com.msg.프로젝트를 삭제 하시겠습니까?', '프로젝트를 삭제 하시겠습니까?'),
        yesCallback: () => {
          openLoading(true);
          deleteMdPrjInfo(prjInfo.elmPrjId ?? '')
            .then((res) => {
              if (res.successOrNot === 'Y') {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.msg.삭제 되었습니다.', '삭제 되었습니다.'),
                });
                btnList();
              } else {
                openMessageBar({
                  type: 'error',
                  content: String(res.data),
                });
              }
            })
            .catch((err) => {
              openMessageBar({
                type: 'error',
                content: t(
                  'com.msg.데이터 저장 중 오류가 발생했습니다.',
                  '데이터 저장 중 오류가 발생했습니다.'
                ),
              });
            })
            .finally(() => openLoading(false));
        },
      });
    };

    const btnDrop = () => {
      if (!prjInfo.elmPrjId) {
        openMessageBar({
          type: 'error',
          content: t('com.msg.프로젝트를 등록하세요.', '프로젝트를 등록하세요.'),
        });
        return;
      }

      openCommonModal({
        modalType: 'confirm',
        content: t('com.msg.프로젝트를 Drop 하시겠습니까?', '프로젝트를 Drop 하시겠습니까?'),
        yesCallback: () => {
          openLoading(true);
          dropMdIves(prjInfo.elmPrjId ?? '')
            .then((res) => {
              if (res.successOrNot === 'Y') {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.msg.프로젝트가 Drop되었습니다.', '프로젝트가 Drop되었습니다.'),
                });
                getData();
              } else {
                openMessageBar({
                  type: 'error',
                  content: String(res.data),
                });
              }
            })
            .catch((err) => {
              openMessageBar({
                type: 'error',
                content: t(
                  'com.msg.데이터 저장 중 오류가 발생했습니다.',
                  '데이터 저장 중 오류가 발생했습니다.'
                ),
              });
            })
            .finally(() => openLoading(false));
        },
      });
    };

    const btnComplete = () => {
      if (!prjInfo.elmPrjId) {
        openMessageBar({
          type: 'error',
          content: t('com.msg.프로젝트를 등록하세요.', '프로젝트를 등록하세요.'),
        });
        return;
      }

      openCommonModal({
        modalType: 'confirm',
        content: t('com.msg.프로젝트를 완료 하시겠습니까?', '프로젝트를 완료 하시겠습니까?'),
        yesCallback: () => {
          openLoading(true);
          completeMdIves(prjInfo.elmPrjId ?? '')
            .then((res) => {
              if (res.successOrNot === 'Y') {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.msg.프로젝트가 완료되었습니다.', '프로젝트가 완료되었습니다.'),
                });
                getData();
              } else {
                openMessageBar({
                  type: 'error',
                  content: String(res.data),
                });
              }
            })
            .catch((err) => {
              openMessageBar({
                type: 'error',
                content: t(
                  'com.msg.데이터 저장 중 오류가 발생했습니다.',
                  '데이터 저장 중 오류가 발생했습니다.'
                ),
              });
            })
            .finally(() => openLoading(false));
        },
      });
    };

    const btnSave = async () => {
      if (!isValid()) {
        return;
      }

      // 전글/셀 라인 수정시 확인
      const chkEltrList = chkEltrLnList.map((o) => o.prdnLnId);
      const chkCellList = chkCellLnList.map((o) => o.prdnLnId);

      const prjEltrList = prjInfo.eltrLines?.map((o) => o.prdnLnId);
      const prjCellList = prjInfo.cellLines?.map((o) => o.prdnLnId);

      let message = t('com.label.저장하시겠습니까?', '저장하시겠습니까?');
      if (
        prjInfo.elmPrjId &&
        (!_.isEqual(chkEltrList, prjEltrList) || !_.isEqual(chkCellList, prjCellList))
      ) {
        message = t(
          'com.label.형개발 투자시 산출에 영향을 주는 항목에 대한 변경이 있습니다.<br/>(수량, 타 사이트 항목, 설비사양 검토 등) 정보 변경 후 변경/초기화 되는 내용 확인 하십시요.',
          '형개발 투자시 산출에 영향을 주는 항목에 대한 변경이 있습니다.<br/>(수량, 타 사이트 항목, 설비사양 검토 등) 정보 변경 후 변경/초기화 되는 내용 확인 하십시요.'
        );
      }

      openCommonModal({
        modalType: 'confirm',
        content: message,
        yesCallback: async () => {
          openLoading(true);
          const fileSaveResponse: FileSaveResponse = await dropzoneRef.current.saveFiles();
          if (fileSaveResponse.fileSaveResult == FileSaveResult.FAIL) return;
          const atchFileGrId = fileSaveResponse.atchFileGrId;

          saveMdPrjInfo({ ...prjInfo, atchFileGrId })
            .then((res) => {
              const newElmPrjId = res.data;
              if (!!newElmPrjId && res.successOrNot === 'Y') {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
                });

                setElmPrjId && setElmPrjId(newElmPrjId);
                if (elmPrjId) getData();
              } else {
                openMessageBar({
                  type: 'error',
                  content: t('com.msg.저장에 실패했습니다.', '저장에 실패했습니다.'),
                });
              }
            })
            .catch((err) => {
              openMessageBar({
                type: 'error',
                content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
              });
            })
            .finally(() => openLoading(false));
        },
      });
    };

    const btnAddCustomer = () => {
      setPopups((prev) => ({ ...prev, customer: true }));
    };

    const closeCustomerPopup = () => {
      initCodes();
      setPopups((prev) => ({ ...prev, customer: false }));
    };

    const openLinePopup = (gbn: string) => {
      if (!prjInfo.plants?.length) {
        setErrors((prev) => ({ ...prev, plants: true }));
        openMessageBar({
          type: 'error',
          content: t('com.msg.플랜트를 선택해주세요.', '플랜트를 선택해주세요.'),
        });
        return;
      }

      setPopups((prev) => ({ ...prev, [gbn]: true }));
    };

    useImperativeHandle(ref, () => ({
      isDirty: () => !_.isEqual(prjInfo, duplPrjInfo),
    }));

    useEffect(() => {
      initCodes();
    }, []);

    useEffect(() => {
      getData();
    }, [elmPrjId]);

    return (
      <>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('md.label.프로젝트 정보', '프로젝트 정보')}</h3>
          </SubTitleGroup>
          <GlobalBtnGroup>
            <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnList}>
              {t('com.button.목록', '목록')}
            </Button>
            {isEditable && (
              <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnReset}>
                {t('com.button.초기화', '초기화')}
              </Button>
            )}
            {isBtnDrop() && (
              <Button
                css={IconButton.button}
                className="save secondary"
                disabled={isLoading}
                onClick={btnDrop}
              >
                {t('com.label.Drop', 'Drop')}
              </Button>
            )}
            {isBtnDelete() && (
              <Button
                css={IconButton.button}
                className="delete"
                disabled={isLoading}
                onClick={btnDelete}
              >
                {t('com.label.삭제', '삭제')}
              </Button>
            )}
            {isBtnInProgress() && (
              <Button
                css={IconButton.button}
                className="save"
                disabled={isLoading}
                onClick={btnInProgress}
              >
                {t('com.label.진행중', '진행중')}
              </Button>
            )}
            {isBtnComplete() && (
              <Button
                css={IconButton.button}
                className="save"
                disabled={isLoading}
                onClick={btnComplete}
              >
                {t('com.label.완료', '완료')}
              </Button>
            )}
            {isBtnSave() && (
              <Button
                css={IconButton.button}
                className="save"
                disabled={isLoading}
                onClick={btnSave}
              >
                {t('com.label.저장', '저장')}
              </Button>
            )}
          </GlobalBtnGroup>
        </SubTitleLayout>
        <TableContainer css={tb.tableCustom} className="left">
          <Table>
            <colgroup>
              <col width={130} />
              <col width={'*'} />
              <col width={130} />
              <col width={'*'} />
              <col width={130} />
              <col width={'*'} />
              <col width={'*'} />
            </colgroup>
            <TableBody>
              <TableRow>
                <TableCell className="header">
                  <span className="dot">{String(t('mp.label.프로젝트명', '프로젝트명'))}</span>
                </TableCell>
                <TableCell colSpan={3}>
                  {isEditable ? (
                    <CustomInputWithSearch
                      name="elmPrjNm"
                      value={prjInfo?.elmPrjNm}
                      onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                      isError={errors?.elmPrjNm}
                      msgError={String(
                        t('md.msg.프로젝트명을 입력해 주세요.', '프로젝트명을 입력해 주세요.')
                      )}
                    />
                  ) : (
                    <span>{prjInfo.elmPrjNm}</span>
                  )}
                </TableCell>
                <TableCell className="header">{String(t('mp.label.작성자', '작성자'))}</TableCell>
                <TableCell colSpan={2}>{prjInfo?.dataInsUserNm}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="header">
                  <span className="dot">{String(t('mp.label.플랜트', '플랜트'))}</span>
                </TableCell>
                <TableCell>
                  {isEditable ? (
                    <MultiComboBox
                      id="plants"
                      options={bildPlntCode}
                      placeholder={String(t('com.label.선택', '선택'))}
                      defaultValue={prjInfo?.plants?.map((o) => o.bildPlntCd || '')}
                      onChange={(value) => handleOnChange('plants', value)}
                      isError={errors?.plants}
                      msgError={String(
                        t('md.msg.플랜트를 선택해 주세요.', '플랜트를 선택해 주세요.')
                      )}
                      readOnly={!isEditable}
                    />
                  ) : (
                    <span>{prjInfo.plants?.map((o) => o.bildPlntCdNm).join(', ')}</span>
                  )}
                </TableCell>
                <TableCell className="header">
                  <span className="dot"></span>
                  {String(t('mp.label.대표 플랜트', '대표 플랜트'))}
                </TableCell>
                <TableCell>
                  {isEditable ? (
                    <ComboBox
                      classNameWrap="grow"
                      options={repPlants}
                      placeholder={String(t('com.label.선택', '선택'))}
                      defaultValue={prjInfo?.repPlntCd}
                      onChange={(value) => handleOnChange('repPlntCd', value)}
                      isError={errors?.repPlntCd}
                      msgError={String(
                        t('md.msg.대표 플랜트를 선택해 주세요.', '대표 플랜트를 선택해 주세요.')
                      )}
                    />
                  ) : (
                    <span>{repPlants?.find((o) => o.cmnCd === prjInfo.repPlntCd)?.cmnCdNm}</span>
                  )}
                </TableCell>
                <TableCell className="header">
                  {String(t('mp.label.작성일자', '작성일자'))}
                </TableCell>
                <TableCell colSpan={2}>{prjInfo?.dataInsDtm}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="header">
                  <span className="dot">{String(t('mp.label.전극 라인', '전극 라인'))}</span>
                </TableCell>
                <TableCell>
                  {isEditable ? (
                    <CustomInputWithSearch
                      name="eltrLnNm"
                      className="find"
                      value={prjInfo?.eltrLnNm}
                      onSearchClick={(e) => openLinePopup('eltrLn')}
                      isError={errors?.eltrLnNm}
                      msgError={String(
                        t('md.msg.전극 라인을 입력해 주세요.', '전극 라인을 입력해 주세요.')
                      )}
                      autoComplete="off"
                    />
                  ) : (
                    <span>{prjInfo?.eltrLnNm}</span>
                  )}
                </TableCell>
                <TableCell className="header">
                  <span className="dot"></span>
                  {String(t('mp.label.대표 라인', '대표 라인'))}
                </TableCell>
                <TableCell>
                  {isEditable ? (
                    <ComboBox
                      classNameWrap="grow"
                      options={repEltrLines}
                      placeholder={String(t('com.label.선택', '선택'))}
                      defaultValue={prjInfo?.repEltrLnId}
                      onChange={(value) => handleOnChange('repEltrLnId', value)}
                      isError={errors?.repEltrLnId}
                      msgError={String(
                        t('md.msg.대표 라인를 선택해 주세요.', '대표 라인를 선택해 주세요.')
                      )}
                    />
                  ) : (
                    <span>
                      {repEltrLines?.find((o) => o.cmnCd === prjInfo.repEltrLnId)?.cmnCdNm}
                    </span>
                  )}
                </TableCell>
                <TableCell className="header">{String(t('mp.label.수정자', '수정자'))}</TableCell>
                <TableCell colSpan={2}>{prjInfo?.dataUpdUserNm}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="header">
                  <span className="dot">{String(t('mp.label.셀 라인', '셀 라인'))}</span>
                </TableCell>
                <TableCell>
                  {isEditable ? (
                    <CustomInputWithSearch
                      name="cellLnNm"
                      className="find"
                      value={prjInfo?.cellLnNm}
                      onSearchClick={(e) => openLinePopup('cellLn')}
                      isError={errors?.cellLnNm}
                      msgError={String(
                        t('md.msg.셀 라인을 입력해 주세요.', '셀 라인을 입력해 주세요.')
                      )}
                      autoComplete="off"
                    />
                  ) : (
                    <span>{prjInfo?.cellLnNm}</span>
                  )}
                </TableCell>
                <TableCell className="header">
                  <span className="dot"></span>
                  {String(t('mp.label.대표 라인', '대표 라인'))}
                </TableCell>
                <TableCell>
                  {isEditable ? (
                    <ComboBox
                      classNameWrap="grow"
                      options={repCellLines}
                      placeholder={String(t('com.label.선택', '선택'))}
                      defaultValue={prjInfo?.repCellLnId}
                      onChange={(value) => handleOnChange('repCellLnId', value)}
                      isError={errors?.repCellLnId}
                      msgError={String(
                        t('md.msg.대표 라인를 선택해 주세요.', '대표 라인를 선택해 주세요.')
                      )}
                    />
                  ) : (
                    <span>
                      {repCellLines?.find((o) => o.cmnCd === prjInfo.repCellLnId)?.cmnCdNm}
                    </span>
                  )}
                </TableCell>
                <TableCell className="header">
                  {String(t('mp.label.수정일자', '수정일자'))}
                </TableCell>
                <TableCell colSpan={2}>{prjInfo?.dataUpdDtm}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="header">
                  <span className="dot">{String(t('mp.label.고객사', '고객사'))}</span>
                </TableCell>
                <TableCell colSpan={3}>
                  {isEditable ? (
                    <div className="flex">
                      <ComboBox
                        classNameWrap="grow text-left"
                        options={customerList}
                        placeholder={String(t('com.label.선택', '선택'))}
                        defaultValue={prjInfo?.custId}
                        onChange={(value) => handleOnChange('custId', value)}
                        isError={errors?.custId}
                        msgError={String(
                          t('md.msg.고객사를 선택해 주세요.', '고객사를 선택해 주세요.')
                        )}
                      />
                      <NormalButton
                        css={IconButton.button}
                        className="icoNone ml-2"
                        size="sm"
                        onClick={btnAddCustomer}
                      >
                        {t('com.button.추가', '추가')}
                      </NormalButton>
                    </div>
                  ) : (
                    <div className="">
                      <span>{customerList.find((o) => o.cmnCd === prjInfo.custId)?.cmnCdNm}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="header">{String(t('mp.label.상태', '상태'))}</TableCell>
                <TableCell colSpan={2}>{prjInfo?.elmPrjStatCdNm}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="header">
                  <span className="dot">{String(t('mp.label.검토 담당자', '검토 담당자'))}</span>
                </TableCell>
                <TableCell colSpan={3}>
                  <div className="">
                    {isEditable ? (
                      <CustomInputWithSearch
                        name="ivesMgr"
                        className="find"
                        value={prjInfo.ivesMgr}
                        isError={errors?.ivesMgr}
                        onSearchClick={(e) => setPopups((prev) => ({ ...prev, ivesMgr: true }))}
                        msgError={String(
                          t('md.msg.검토 담당자를 선택해 주세요.', '검토 담당자를 선택해 주세요.')
                        )}
                        autoComplete="off"
                      />
                    ) : (
                      <span>{prjInfo.ivesMgr}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="header" rowSpan={2}>
                  {String(t('mp.label.첨부파일', '첨부파일'))}
                </TableCell>
                <TableCell rowSpan={2} colSpan={2}>
                  <div style={{ minWidth: '400px' }}>
                    <Dropzone
                      ref={dropzoneRef}
                      atchFileGrIdInput={prjInfo?.atchFileGrId}
                      atchFileTpCd="MD_PRJ_INFO"
                      downloadOnly={!isEditable}
                    />
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="header">
                  <span className="dot">
                    {String(t('mp.label.마케팅팀 담당자', '마케팅팀 담당자'))}
                  </span>
                </TableCell>
                <TableCell colSpan={3}>
                  <div className="">
                    {isEditable ? (
                      <CustomInputWithSearch
                        name="elmPrjId"
                        className="find"
                        value={prjInfo.mktgMgr}
                        isError={errors?.mktgMgr}
                        onSearchClick={(e) => setPopups((prev) => ({ ...prev, mktgMgr: true }))}
                        msgError={String(
                          t(
                            'md.msg.마케팅팀 담당자를 선택해 주세요.',
                            '마케팅팀 담당자를 선택해 주세요.'
                          )
                        )}
                        autoComplete="off"
                      />
                    ) : (
                      <span>{prjInfo.mktgMgr}</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="header">
                  <span className="dot">
                    {String(t('mp.label.프로젝트 상세', '프로젝트 상세'))}
                  </span>
                </TableCell>
                <TableCell colSpan={6}>
                  {isEditable ? (
                    <CustomTextarea
                      name={'elmPrjDesc'}
                      value={prjInfo?.elmPrjDesc}
                      isError={errors?.elmPrjDesc}
                      wrapStyle={{
                        height: '250px',
                      }}
                      onChange={(e) => handleOnChange('elmPrjDesc', e.target.value)}
                      msgError={String(
                        t('md.msg.프로젝트 상세를 입력해 주세요.', '프로젝트 상세를 입력해 주세요.')
                      )}
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: 'left',
                        minHeight: '250px',
                        height: '250px',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word',
                        overflowY: 'auto',
                      }}
                    >
                      {prjInfo.elmPrjDesc}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {popups.cellLn && prjInfo.plants?.length && (
          <EltrCellLineInqPopup
            open={popups.cellLn}
            close={() => setPopups((prev) => ({ ...prev, cellLn: false }))}
            selectedItems={prjInfo.cellLines || []}
            condition={{
              bildPlntCds: prjInfo.plants?.map((o) => o.bildPlntCd || ''),
              prdnLnTpCd: 'C',
              prdnLnTpCdNm: 'Cell',
            }}
            callbackFn={(selectedItems: MdLine[]) => {
              setPrjInfo((prev) => ({
                ...prev,
                cellLines: [...selectedItems],
                cellLnNm: [...selectedItems].map((o) => o.bildPlntCd + ' ' + o.prdnLnNm).join(', '),
                repCellLnId: selectedItems.some((o) => o.prdnLnId === prev.repCellLnId)
                  ? prev.repCellLnId
                  : selectedItems[0]?.prdnLnId || '',
              }));
              setErrors((prev) => ({ ...prev, cellLnNm: false, repCellLnId: false }));
            }}
          />
        )}
        {popups.eltrLn && prjInfo.plants?.length && (
          <EltrCellLineInqPopup
            open={popups.eltrLn}
            close={() => setPopups((prev) => ({ ...prev, eltrLn: false }))}
            selectedItems={prjInfo.eltrLines || []}
            condition={{
              bildPlntCds: prjInfo.plants?.map((o) => o.bildPlntCd || ''),
              prdnLnTpCd: 'E',
              prdnLnTpCdNm: '전극',
            }}
            callbackFn={(selectedItems: MdLine[]) => {
              setPrjInfo((prev) => ({
                ...prev,
                eltrLines: [...selectedItems],
                eltrLnNm: [...selectedItems].map((o) => o.bildPlntCd + ' ' + o.prdnLnNm).join(', '),
                repEltrLnId: selectedItems.some((o) => o.prdnLnId === prev.repEltrLnId)
                  ? prev.repEltrLnId
                  : selectedItems[0]?.prdnLnId || '',
              }));
              setErrors((prev) => ({ ...prev, eltrLnNm: false, repEltrLnId: false }));
            }}
          />
        )}
        {popups.customer && (
          <CustomerRegistPopup open={popups.customer} close={closeCustomerPopup} />
        )}
        {popups.ivesMgr && (
          <UserMultiModal
            open={popups.ivesMgr}
            close={() => setPopups((prev) => ({ ...prev, ivesMgr: false }))}
            title={t('com.label.검토 담당자 조회', '검토 담당자 조회')}
            condition={{
              roles: ['MD_IVES_MANAGER'],
            }}
            defaultUserId={prjInfo.ivesManagers?.map((o) => o.wrtUserId).join(', ')}
            singleSelect={false}
            onCallback={(callbackData: any) => {
              if (callbackData.userId === '') {
                return false;
              }

              setPrjInfo((prev) => ({
                ...prev,
                ivesManagers: callbackData.userId.split(',').map((o: string) => ({ wrtUserId: o })),
                ivesMgr: callbackData.empNm,
              }));
              setErrors((prev) => ({ ...prev, ivesMgr: false }));
            }}
          />
        )}
        {popups.mktgMgr && (
          <UserMultiModal
            open={popups.mktgMgr}
            close={() => setPopups((prev) => ({ ...prev, mktgMgr: false }))}
            title={t('com.label.마케팅팀 담당자 조회', '마케팅팀 담당자 조회')}
            condition={{}}
            defaultUserId={prjInfo.mktgManagers?.map((o) => o.wrtUserId).join(', ')}
            singleSelect={false}
            onCallback={(callbackData: any) => {
              if (callbackData.userId === '') {
                return false;
              }

              setPrjInfo((prev) => ({
                ...prev,
                mktgManagers: callbackData.userId.split(',').map((o: string) => ({ wrtUserId: o })),
                mktgMgr: callbackData.empNm,
              }));
              setErrors((prev) => ({ ...prev, mktgMgr: false }));
            }}
          />
        )}
      </>
    );
  }
);

PrjInfoPage.displayName = 'PrjInfoPage';

export default PrjInfoPage;
